import { FilterDropdown, TextField, useSelect, useTable } from "@refinedev/antd";
import { Button, Select, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { ILicence, IMetadataOther, IVessel } from "interfaces";
import React, { useState } from "react";
import {
  VISIBLE_OTHER_SERVICE_CATALOG,
  OTHER_SERVICES_BY_PRODUCT,
  OTHER_PRODUCT_COLORS,
  OtherServiceDef,
  WIFI_DONGLE_VISIBLE,
  readOtherField,
  fmtAnnualFee,
  fmtCurrency,
  fmtDiscountOrAmount,
  fmtWifiDongle,
} from "components/metadata/otherServiceCatalog";

const { Text } = Typography;

interface ServiceRow {
  key: string;
  service: string;
  subscriptionType: string;
  currency: string;
  annualFlatFee: string;
  discount: string;
  wifiDongle?: string;
}

const buildActiveOtherServiceRows = (record: ILicence): ServiceRow[] => {
  const others = record.subscription?.metadataOthers;
  return VISIBLE_OTHER_SERVICE_CATALOG.filter(
    (svc) => readOtherField<string>(others, `${svc.prefix}Status`) === "active",
  ).map((svc) => ({
    key: svc.prefix,
    service: `${svc.product} — ${svc.service}`,
    subscriptionType:
      readOtherField<string>(others, `${svc.prefix}SubscriptionType`) ?? "—",
    currency: fmtCurrency(svc, others),
    annualFlatFee: fmtAnnualFee(svc, others),
    discount: fmtDiscountOrAmount(svc, others),
    wifiDongle:
      svc.hasWifiDongle && WIFI_DONGLE_VISIBLE ? fmtWifiDongle(others) : undefined,
  }));
};

const getActiveServices = (record: ILicence): OtherServiceDef[] => {
  const others = record.subscription?.metadataOthers;
  return VISIBLE_OTHER_SERVICE_CATALOG.filter(
    (svc) => readOtherField<string>(others, `${svc.prefix}Status`) === "active",
  );
};

export const OtherServiceSubscriptionDetails: React.FC = () => {
  const [selectedService, setSelectedService] = useState<string>();
  const { tableProps, setFilters, filters } = useTable<ILicence>({
    resource: "licence",
    pagination: { pageSize: 10 },
    sorters: {
      initial: [{ field: "id", order: "desc" }],
    },
  });

  const { selectProps: imoSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "imoNumber",
    optionValue: "imoNumber",
    pagination: { mode: "server" },
  });

  const { selectProps: vesselSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "name",
    pagination: { mode: "server" },
  });

  const expandedRowRender = (record: ILicence) => {
    const rows = buildActiveOtherServiceRows(record);
    const nestedColumns: ColumnsType<ServiceRow> = [
      { title: "Service", dataIndex: "service", key: "service", width: 360 },
      {
        title: "Subscription Type",
        dataIndex: "subscriptionType",
        key: "subscriptionType",
        width: 200,
      },
      {
        title: "Currency",
        dataIndex: "currency",
        key: "currency",
        width: 110,
      },
      {
        title: "Annual Flat Fee",
        dataIndex: "annualFlatFee",
        key: "annualFlatFee",
        width: 160,
      },
      {
        title: "Discount / Per-Use Amount",
        dataIndex: "discount",
        key: "discount",
        width: 180,
      },
    ];
    return (
      <Table
        columns={nestedColumns}
        dataSource={rows}
        pagination={false}
        size="small"
        rowKey="key"
        expandable={{
          expandedRowRender: (row) =>
            row.wifiDongle ? (
              <Text type="secondary">{row.wifiDongle}</Text>
            ) : null,
          rowExpandable: (row) => !!row.wifiDongle,
        }}
      />
    );
  };

  const columns: ColumnsType<ILicence> = [
    {
      title: "IMO Number",
      dataIndex: ["vessel", "imoNumber"],
      key: "vessel.imoNumber",
      width: 130,
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            placeholder="Select IMO"
            {...imoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Vessel Name",
      dataIndex: ["vessel", "name"],
      key: "vessel.name",
      width: 200,
      ellipsis: { showTitle: true },
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            placeholder="Select Vessel"
            {...vesselSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Active Services",
      filterDropdown: ({ confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            style={{ width: 260, marginBottom: 8, display: "block" }}
            placeholder="Select Service"
            allowClear
            value={selectedService}
            onChange={(value) => setSelectedService(value)}
          >
            {OTHER_SERVICES_BY_PRODUCT.map((group) => (
              <Select.OptGroup key={group.product} label={group.product}>
                {group.services.map((svc) => (
                  <Select.Option
                    key={svc.prefix}
                    value={`subscription.metadataOthers.${svc.prefix}Status`}
                  >
                    {svc.service}
                  </Select.Option>
                ))}
              </Select.OptGroup>
            ))}
          </Select>
          <Space>
            <Button
              type="primary"
              size="small"
              onClick={() => {
                const serviceFields = VISIBLE_OTHER_SERVICE_CATALOG.map(
                  (svc) => `subscription.metadataOthers.${svc.prefix}Status`,
                );
                const otherFilters = (filters || []).filter(
                  (f) =>
                    !("field" in f && serviceFields.includes(f.field as string)),
                );
                const nextFilters = selectedService
                  ? [
                      ...otherFilters,
                      {
                        field: selectedService,
                        operator: "eq" as const,
                        value: "active",
                      },
                    ]
                  : otherFilters;
                setFilters(nextFilters, "replace");
                confirm();
              }}
            >
              Filter
            </Button>
            <Button
              size="small"
              onClick={() => {
                const serviceFields = VISIBLE_OTHER_SERVICE_CATALOG.map(
                  (svc) => `subscription.metadataOthers.${svc.prefix}Status`,
                );
                const otherFilters = (filters || []).filter(
                  (f) =>
                    !("field" in f && serviceFields.includes(f.field as string)),
                );
                setSelectedService(undefined);
                setFilters(otherFilters, "replace");
                clearFilters?.();
              }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      render: (_, record) => {
        const active = getActiveServices(record);
        if (active.length === 0) {
          return <Text type="secondary">None</Text>;
        }
        return active.map((svc) => (
          <Tag key={svc.prefix} color={OTHER_PRODUCT_COLORS[svc.product]}>
            {svc.service}
          </Tag>
        ));
      },
    },
  ];

  return (
    <Table
      {...tableProps}
      columns={columns}
      rowKey="id"
      size="middle"
      expandable={{
        expandedRowRender,
        rowExpandable: (record) => getActiveServices(record).length > 0,
      }}
      pagination={{
        ...tableProps.pagination,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
      }}
    />
  );
};
